import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useForm } from '@weezevent/weezjs-core';
import { requiredField } from '../../../../../utils/validators';

import { ModerationTransitions, ModerationActions } from '../../../../../models';

import { Card, WeezHeader, Button, ToastContext, HeaderCard, TitleCard, SubTitleCard, ContentCard } from '@weezevent/nacre';

import { SettingsStep, ActionsStep, OperatorsStep } from './steps';
import { ALLOWED_ACTIONS } from './actions/ActionsHandler';

export default function TransitionDetails({ organization, flowId, transitionId }) {
    const { t } = useTranslation();
    const history = useHistory();
    const toast = React.useContext(ToastContext);
    const transitionExists = React.useMemo(() => !!transitionId, [transitionId]);
    const [actionsInfos, setActionsInfos] = React.useState([]);

    const [form, setForm, setFormObject] = useForm(
        {
            name: '',
            source_status: null,
            destination_status: null,
            actions: [],
            initialActions: [],
            allowed_users: [],
            id: null,
            display: true
        },
        {
            source_status: requiredField,
            destination_status: requiredField
        }
    );

    ModerationTransitions.useApiModel(
        {
            organizationId: organization.id,
            flowId,
            id: transitionId,
            launch: Boolean(transitionId) && !form.id,
            cache: false,
            allow_cache: false,
            onSuccess: ({ object }) => {
                setFormObject({
                    ...form,
                    ...object,
                    actions: [...object.actions.filter(ac => ALLOWED_ACTIONS.includes(ac.type))],
                    initialActions: [...object.actions],
                    destination_status: object.destination_status.id,
                    source_status: object.source_status.id,
                    allowed_users: object.allowed_users.map(user => parseInt(user.id))
                });
            }
        },
        [organization, flowId, transitionId]
    );

    const steps = React.useMemo(
        () => [
            {
                key: 'settings',
                title: t('ticket.moderation.statusesTransitions.transition.steps.settings.title'),
                subtitle: t('ticket.moderation.statusesTransitions.transition.steps.settings.subtitle'),
                summary: `${t('ticket.moderation.statusesTransitions.transition.steps.settings.summary')} ${form.name}`,
                component: (
                    <SettingsStep
                        form={form}
                        flowId={flowId}
                        setForm={setForm}
                        organization={organization}
                        onDone={onCardDone => onCardDone()}
                        transitionExists={transitionExists}
                    />
                )
            },
            {
                key: 'actions',
                title: t('ticket.moderation.statusesTransitions.transition.steps.actions.title'),
                subtitle: t('ticket.moderation.statusesTransitions.transition.steps.actions.subtitle'),
                summary: (
                    <span>
                        {t('ticket.moderation.statusesTransitions.transition.steps.actions.summary')}
                        {form.actions.length}
                    </span>
                ),
                onEdit: () => setForm('actions', actionsInfos),
                component: (
                    <ActionsStep
                        form={form}
                        flowId={flowId}
                        setForm={setForm}
                        organization={organization}
                        transitionId={transitionId}
                        setActionsInfos={setActionsInfos}
                        onDone={onCardDone => onCardDone()}
                        transitionExists={transitionExists}
                    />
                )
            },
            {
                key: 'operators',
                title: t('ticket.moderation.statusesTransitions.transition.steps.operators.title'),
                subtitle: t('ticket.moderation.statusesTransitions.transition.steps.operators.subtitle'),
                component: <OperatorsStep organizationId={organization.id} flowId={flowId} form={form} setForm={setForm} transitionExists={transitionExists} />
            }
        ],
        [form, organization, flowId, transitionExists]
    );

    const handleSubmit = React.useCallback(async () => {
        let errors = [];
        let ids = [];
        const actions = form.actions.map(action => new ModerationActions(action));
        const actionsPromise = actions.map(async action => {
            await action
                .save({ organizationId: organization.id, flowId, transitionId: form.id })
                .then(({ response }) => {
                    ids.push(response.id);
                    toast.success(t('ticket.moderation.statusesTransitions.transition.steps.messageSuccessSave'));
                })
                .catch(() => {
                    errors.push(action);
                });
        });

        await Promise.all(actionsPromise);

        if (!errors.length) {
            let transition = new ModerationTransitions({ ...form });
            const ignoredActions = form.initialActions.filter(ac => !ALLOWED_ACTIONS.includes(ac.type)).map(ac => ac.id);
            const actions = Array.from(new Set([...form.actions.map(ac => ac.id), ...ignoredActions, ...ids])).filter(Boolean);
            setForm('actions', actions);

            transition.actions = actions;
            transition.save({ organizationId: organization.id, flowId }).catch(() => {
                toast.error(t('ticket.moderation.statusesTransitions.transition.steps.messageErrorSave'));
            });

            history.push(`/ticket/O${organization.id}/moderation/moderation_flows/${flowId}/statuses_transitions/transitions`);
        } else {
            toast.error(t('ticket.moderation.statusesTransitions.transition.steps.messageErrorSave'));
        }
    }, [form, organization.id, flowId]);

    return (
        <>
            <WeezHeader
                title={t(`ticket.moderation.statusesTransitions.transition.${transitionExists ? 'editTitle' : 'createTitle'}`)}
                rightComponent={transitionExists ? [<Button key={0} primary label={t('ticket.common.action.save')} onClick={() => handleSubmit()} />] : []}
                backLink={{
                    onClick: () => {
                        history.push(`/ticket/O${organization.id}/moderation/moderation_flows/${flowId}/statuses_transitions/transitions`);
                    }
                }}
            />
            {transitionExists ? <TransitionPage steps={steps} /> : <TransitionSteps steps={steps} />}
        </>
    );
}

const TransitionSteps = ({ steps }) => {
    const { t } = useTranslation();
    return (
        <Card.FormManager labelPreviousStepMobile={t('ticket.common.back_step')} labelStepMobile={t(['ticket.common.step'])} withStepsMobile>
            {steps.map(step => (
                <Card.FormStep
                    key={step.key}
                    title={step.title}
                    summary={step.summary}
                    subtitle={step.subtitle}
                    editLabel={t('ticket.common.action.edit')}
                    onEdit={step.onEdit ? () => step.onEdit() : undefined}
                >
                    {step.component}
                </Card.FormStep>
            ))}
        </Card.FormManager>
    );
};

const TransitionPage = ({ steps }) => {
    return steps.map(step => (
        <Card key={step.key}>
            <HeaderCard separator>
                <TitleCard title={step.title} />
                <SubTitleCard title={step.subtitle} />
            </HeaderCard>
            <ContentCard>{step.component}</ContentCard>
        </Card>
    ));
};
